import { Check, Lock, Star, Crown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useCallback, useEffect, useState } from "react";
import { useOrganizationStore } from "@/store";
import { getPackages } from "@/services/BillingService";
import { TPackagesGetResponse } from "@shoutout-labs/market_buzz_crm_types";

interface FeatureItemProps {
  text: string;
  included: boolean;
}

const FeatureItem = ({ text, included }: FeatureItemProps) => (
  <div className="flex items-center gap-3 py-2">
    {included ? (
      <Check className="h-4 w-4 text-green-600" />
    ) : (
      <Lock className="h-4 w-4 text-gray-400" />
    )}
    <span className={`text-sm ${included ? "text-gray-700" : "text-gray-500"}`}>
      {text}
    </span>
  </div>
);

export default function SubscriptionPage() {
  const { organization } = useOrganizationStore();
  const [packages, setPackages] = useState<TPackagesGetResponse["items"]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await getPackages({
          limit: 10,
          skip: 0
        });
        setPackages(response.items);
      } catch (error) {
        console.error("Failed to fetch packages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  const handleSubscribe = useCallback(() => {
    window.open(
      `https://clover.com/appmarket/m/${organization.referenceId}/apps/${organization.appReferenceId}`,
      "_blank"
    );
  }, [organization]);

  // Get freemium and premium packages from the API response
  const freemiumPackage = packages.find(
    (pkg) => (pkg as any).packageName === "Freemium"
  );
  const premiumPackage = packages.find(
    (pkg) => (pkg as any).packageName === "Premium"
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-in fade-in duration-700">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Perfect Plan
          </h1>
          <p className="text-lg text-gray-600">
            Unlock powerful insights and analytics with our flexible pricing
            options
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto items-stretch">
          {/* Freemium Card */}
          <div className="relative animate-in slide-in-from-left-4 duration-500">
            <Card className="relative border-2 border-green-200 bg-white rounded-3xl shadow-lg h-full flex flex-col transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-green-300">
              <div className="absolute -top-3 left-4 z-10">
                <Badge className="bg-green-600 text-white px-3 py-1 text-xs font-medium">
                  Current Plan
                </Badge>
              </div>
              <CardHeader className="text-center pb-6 pt-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {(freemiumPackage as any)?.packageName || "Freemium"}
                </h2>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">
                    ${freemiumPackage?.dollarValue || 0}
                  </span>
                  <span className="text-gray-600">/month</span>
                </div>
                <p className="text-sm text-gray-600">
                  Perfect for getting started with basic Insight Unlock features
                </p>
              </CardHeader>
              <CardContent className="space-y-2 flex-1">
                <FeatureItem text="Dashboard full access" included={true} />
                <FeatureItem text="Customer Reports Overview" included={true} />
                <FeatureItem text="Sales Reports Overview" included={true} />
                <FeatureItem text="Transaction Trend Heatmap" included={true} />
                <FeatureItem
                  text="Extended Reports & Trends"
                  included={false}
                />
                <FeatureItem text="Customers module" included={false} />
                <FeatureItem text="Broadcast campaigns" included={false} />
                <FeatureItem text="Customer import" included={false} />
                <FeatureItem text="Transaction list" included={false} />
                <FeatureItem text="Automated campaigns" included={false} />
                <FeatureItem text="Broadcast SMS Reports" included={false} />
                <FeatureItem text="Product list" included={false} />
              </CardContent>
              <div className="p-6 pt-0">
                <Button
                  variant="outline"
                  className="w-full bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                  disabled
                >
                  You are currently on this plan
                </Button>
              </div>
            </Card>
          </div>

          {/* Premium Card */}
          <div className="relative animate-in slide-in-from-right-4 duration-500 delay-200">
            <Card className="relative border-2 border-purple-200 bg-white rounded-3xl shadow-lg h-full flex flex-col transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-purple-300">
              <div className="absolute -top-3 left-4 z-10">
                <Badge className="bg-purple-600 text-white px-3 py-1 text-xs font-medium flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Recommended
                </Badge>
              </div>
              <CardHeader className="text-center pb-6 pt-8">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {(premiumPackage as any)?.packageName || "Premium"}
                  </h2>
                  <Crown className="h-5 w-5 text-purple-600" />
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">
                    ${premiumPackage?.dollarValue || 29}
                  </span>
                  <span className="text-gray-600">/month</span>
                </div>
                <p className="text-sm text-gray-600">
                  Enhanced analytics with Extended Reports & Trends for deeper
                  insights
                </p>
              </CardHeader>
              <CardContent className="space-y-2 flex-1">
                <FeatureItem text="Dashboard full access" included={true} />
                <FeatureItem text="Customer Reports Overview" included={true} />
                <FeatureItem text="Sales Reports Overview" included={true} />
                <FeatureItem text="Extended Reports & Trends" included={true} />
                <FeatureItem text="Customers module" included={false} />
                <FeatureItem text="Broadcast campaigns" included={false} />
                <FeatureItem text="Customer import" included={false} />
                <FeatureItem text="Transaction list" included={false} />
                <FeatureItem text="Automated campaigns" included={false} />
                <FeatureItem text="Broadcast SMS Reports" included={false} />
                <FeatureItem text="Product list" included={false} />
              </CardContent>

              <div className="p-6 pt-0">
                <Button
                  onClick={handleSubscribe}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 rounded-lg transition-colors duration-200"
                >
                  Upgrade to Premium
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
